import axios, {
  AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from "axios";

import { obterAccessToken, useSessaoStore } from "../auth/store";
import { normalizarErroAxios, redigirAuthorization } from "./erros";
import { refreshComSingleFlight } from "./refresh";

/*
 * HTTP client do painel (CP-C — ADR 0026 §HTTP client).
 *
 * - Request interceptor: injeta `Authorization: Bearer <access>` lido do store
 *   memory-only. Sem token → não injeta header.
 * - Response interceptor: 401 no access → refresh single-flight (I0) + replay
 *   UMA vez com o token novo; 401 no refresh OU 401 no replay (token novo já
 *   morto) → limpa store + `onSessaoExpirada` + propaga sem retry; 503/404 →
 *   erro normalizado (sem refresh, sem vazar distinção); I4 → `Authorization` e
 *   tokens no body redigidos antes de qualquer propagação.
 *
 * DESACOPLAMENTO DO ROUTER: o interceptor NÃO navega. O sinal de "vá para
 * /login" é o callback injetável `onSessaoExpirada` (default no-op). O CP-D
 * pluga a navegação real via `configurarSessaoExpirada`.
 */

/** Handler de sessão expirada — o CP-D pluga a navegação real aqui. */
type HandlerSessaoExpirada = () => void;

let onSessaoExpirada: HandlerSessaoExpirada = () => {
  // no-op por padrão: não há router no CP-C.
};

/** Configura o handler de sessão expirada (chamado pelo CP-D ao montar o router). */
export function configurarSessaoExpirada(handler: HandlerSessaoExpirada): void {
  onSessaoExpirada = handler;
}

/**
 * Encerra a sessão: zera o store de tokens e sinaliza a navegação para /login.
 *
 * Fonte ÚNICA do "fim de sessão" — os DOIS ramos 401 do interceptor (401 no
 * refresh e replay-401 esgotado) chamam este helper. Mantém o par
 * `limpar()` + `onSessaoExpirada()` indivisível: nunca limpar o store sem
 * sinalizar /login (senão fica "logado" com token morto = limbo), nem sinalizar
 * sem limpar. NÃO muda o comportamento — só elimina a duplicação (DRY).
 */
export function encerrarSessao(): void {
  useSessaoStore.getState().limpar();
  onSessaoExpirada();
}

/** Flag por-request: já tentamos o replay pós-refresh? Evita loop de retry. */
interface ConfigComRetry extends InternalAxiosRequestConfig {
  _jaTentouRefresh?: boolean;
}

export const cliente: AxiosInstance = axios.create({
  baseURL: "/api",
});

/*
 * Request interceptor: injeta o Bearer do store memory-only. Sem token, não
 * injeta header (requests anônimas — ex.: login — passam limpas).
 */
cliente.interceptors.request.use((config) => {
  const access = obterAccessToken();
  if (access !== null) {
    config.headers.set("Authorization", `Bearer ${access}`);
  }
  return config;
});

/*
 * Response interceptor: o ciclo de sessão. Tudo que não for 401-replay-bem-
 * sucedido sai como `ErroHttp` normalizado (token já redigido).
 */
cliente.interceptors.response.use(
  (resposta) => resposta,
  async (erro: unknown) => {
    if (!(erro instanceof AxiosError)) {
      // Erro não-axios (bug de código): propaga como está, sem token envolvido.
      return Promise.reject(erro);
    }

    const config = erro.config as ConfigComRetry | undefined;
    const status = erro.response?.status ?? null;

    // 401 no ACCESS → fluxo de refresh single-flight + replay UMA vez.
    // `config?._jaTentouRefresh` garante no máximo 1 replay por request original.
    if (status === 401 && config && !config._jaTentouRefresh) {
      config._jaTentouRefresh = true;
      try {
        const novoAccess = await refreshComSingleFlight();
        // Replay com o token NOVO. `set` sobrescreve o Authorization antigo.
        config.headers.set("Authorization", `Bearer ${novoAccess}`);
        return cliente.request(config);
      } catch (erroRefresh) {
        // 401 no refresh (família revogada / refresh inválido) OU ausência de
        // refresh: sessão acabou. Encerra a sessão (limpa store + sinaliza
        // /login), propaga sem novo retry. O reset da promise em voo já ocorreu
        // no `.finally` do single-flight.
        encerrarSessao();
        // Normaliza o erro do refresh (Authorization redigido) e propaga.
        const causa =
          erroRefresh instanceof AxiosError
            ? normalizarErroAxios(erroRefresh)
            : normalizarErroAxios(erro);
        return Promise.reject(causa);
      }
    }

    // 401 no REPLAY (refresh deu certo, mas o request com o token novo voltou
    // 401): `_jaTentouRefresh` já é true → o refresh esgotou seu valor. NÃO
    // tentamos de novo (evita loop). Espelha o tratamento do catch acima: limpa
    // o store, sinaliza /login e propaga SessaoExpirada — senão o store fica
    // "logado" com um token morto (limbo).
    if (status === 401 && config?._jaTentouRefresh) {
      encerrarSessao();
      redigirAuthorization(config);
      return Promise.reject(normalizarErroAxios(erro));
    }

    // 503 / 404 / demais: NÃO dispara refresh. Normaliza (token redigido) sem
    // vazar distinção IDOR vs. inexistente (404) nem motivo do 503.
    redigirAuthorization(config);
    return Promise.reject(normalizarErroAxios(erro));
  },
);
